import { UIColor, UIRoute, UIView, YES } from "uicore-ts"
// @ts-ignore
import { RHProcurementOffer, RHProcurementPartOffer } from "../../../../models/OfferModels"
import { RHProcurement, RHProcurementPart } from "../../../../models/ProcurementModels"
import { RHCore } from "../../Core/RHCore"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHDetailsDialogViewController } from "../../Custom components/RH/RHDetailsDialogViewController"

import { OfferDetailDialogView } from "./OfferDetailDialogView"
import { populatedValueOfReference, populatedValuesOfReferences } from "../../Core/RHReference"


// State is delivered via a module-level store (set immediately before
// navigating to the route) because the callback objects can't be encoded in a
// URL. On a hard refresh the store is empty, so the controller self-loads from
// the server using the offerId route parameter.

interface OfferDetailState {
    partOffers: RHProcurementPartOffer[]
    initialIndex: number
    part: RHProcurementPart
    isEvaluationFinalizable: boolean
    evaluationDidChange: () => void
    persistPartOfferEvaluation: (partOffer: RHProcurementPartOffer, onError?: () => void) => Promise<void>
    procurementOfferId?: string
    partCount?: number
    // Called when the dialog demotes the currently-displayed offer away from
    // "chosenAsWinner" because it no longer meets requirements, so the
    // section owning the live winner-selection state can clear it too.
    winnerWasCleared?: (clearedPartOfferId: string) => void
    // Called if that demotion's server write is then rejected, so the section
    // can restore the winner it optimistically cleared.
    winnerWasRestored?: (restoredPartOfferId: string) => void
    canAnswerMessages?: boolean
}

let _pendingOfferDetailState: OfferDetailState | undefined

export function registerPendingOfferDetailState(state: OfferDetailState): void {
    _pendingOfferDetailState = state
}


export class OfferDetailViewController extends RHDetailsDialogViewController {
    
    static override readonly routeComponentName = "offerDetail"
    static override readonly ParameterIdentifierName = {
        offerId: "offerId"
    }
    
    protected override readonly maxWidth = 860
    protected override readonly widthViewportRatio = 0.9
    
    readonly dialogView = new OfferDetailDialogView()
        .addedAsSubviewToView(this.view)
    protected override readonly contentView = this.dialogView
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        if (this.dialogView._focusManager) {
            items.push(this.dialogView._focusManager)
        }
        return items
    })
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: UIColor.transparentColor
        })
        
        // UIDialogView only detects taps on the backdrop element itself. This
        // transparent view covers the backdrop around the dialog box, so taps
        // there need to dismiss too — but only when they land on the backdrop,
        // not inside the dialog box.
        // noinspection JSConstantReassignment
        this.view.controlEventTargetAccumulator.PointerTap = (_sender: UIView, event: Event) => {
            if (event.target === this.view.viewHTMLElement) {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(OfferDetailViewController.routeComponentName)
                    .apply()
            }
        }
    }
    
    
    override async viewWillAppear(): Promise<void> {
        await super.viewWillAppear()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
        
        const state = _pendingOfferDetailState
        _pendingOfferDetailState = undefined
        
        if (state) {
            this.dialogView.configure(
                state.partOffers,
                state.initialIndex,
                state.part,
                state.isEvaluationFinalizable,
                state.evaluationDidChange,
                state.persistPartOfferEvaluation,
                state.procurementOfferId,
                state.partCount,
                state.winnerWasCleared,
                state.winnerWasRestored,
                state.canAnswerMessages === YES
            )
        }
        else {
            // Hard refresh with offerDetail already in the URL — self-load.
            void this._loadFromServer()
        }
    }
    
    override async viewWillDisappear(): Promise<void> {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.popLayer()
    }
    
    
    private async _loadFromServer(): Promise<void> {
        const procurementOfferId = UIRoute.currentRoute
            .componentWithViewController(OfferDetailViewController)
            ?.parameters.offerId
        
        const procurementId = UIRoute.currentRoute
            .componentWithName("procurement_details")
            ?.parameters.id
        
        if (!procurementOfferId || !procurementId) {
            return
        }
        
        try {
            const procurementInformation = await RHCore.sharedInstance.procurementStore
                .retrieveProcurementInformation(procurementId)
            const procurement = procurementInformation.procurement as RHProcurement
            const capabilities = procurementInformation.capabilities
            if (capabilities?.canViewDetailed !== YES) {
                return
            }
            
            const matchingOffer = await RHCore.sharedInstance.offerStore
                .retrieveIncomingOfferWithIdForProcurement(procurementOfferId, procurementId)
            if (!matchingOffer) {
                return
            }
            
            // Annotate each part offer with the company name, mirroring _buildPartSections.
            const companyName = populatedValueOfReference(matchingOffer.offererCompany)?.name ?? "Tundmatu ettevõte"
            const partOffers = populatedValuesOfReferences(matchingOffer.partOffers)
            for (const partOffer of partOffers) {
                partOffer.offererCompanyName = companyName
            }
            
            if (partOffers.length === 0) {
                return
            }
            
            const firstPartOffer = partOffers[0]
            const partIdStr = firstPartOffer.partId?.toString()
            const parts: RHProcurementPart[] = procurement.parts ?? []
            const matchingPart = parts.find(part => part._id.toString() === partIdStr) ?? parts[0]
            
            const deadlinePassed = new Date(procurement.deadlineDate ?? Date.now()).getTime() < Date.now()
            const isPartFinalized = (procurement.finalizedPartIds ?? []).includes(matchingPart._id.toString())
            const isEvaluationFinalizable =
                capabilities?.canEvaluate === YES &&
                procurement.status === "published" &&
                deadlinePassed &&
                !isPartFinalized
            
            this.dialogView.configure(
                partOffers,
                0,
                matchingPart,
                isEvaluationFinalizable,
                () => { /* no live table to refresh on a hard reload */ },
                async (partOffer: RHProcurementPartOffer) => {
                    await RHCore.sharedInstance.offerStore.updatePartOfferEvaluation({
                        partOfferId: partOffer._id + "",
                        meetsRequirements: partOffer.meetsRequirements,
                        evaluationNotes: partOffer.evaluationNotes ?? "",
                        decision: partOffer.decision ?? "undecided"
                    })
                },
                procurementOfferId,
                parts.length,
                undefined,
                undefined,
                capabilities?.canAnswerMessages === YES
            )
        }
        catch (error) {
            console.error("OfferDetailViewController: failed to self-load offer data", error)
        }
    }
    
}
